import moment from "moment-timezone";
import { apiStore } from "../stores/api.store";
import { CertificationFilterValue } from "../components/Shared/CertificationReportFilter/CertificationReportFilter";

const timeZone = moment.tz.guess();

/**
 * Spread the certification filter onto a report query. The backend only applies
 * status/expiresBy once a type is picked, so nothing is sent without one.
 */
const certificationQuery = (certification?: CertificationFilterValue) => {
	if (!certification?.typeKey) return {};

	return {
		certificationType: certification.typeKey,
		...(certification.status ? { certificationStatus: certification.status } : {}),
		...(certification.expiresBy
			? { certificationExpiresBy: moment(certification.expiresBy).format("YYYY-MM-DD") }
			: {}),
	};
};

/**
 * Report export endpoints. Each call returns a URL (relative to the API root)
 * to the generated report file, or undefined on failure.
 */
export const reportStore = {
	async getCustomersReport(pool?: string, active?: boolean): Promise<string | undefined> {
		let query: { [x: string]: string } = { timeZone };

		if (pool) query.pool = pool;

		if (active !== undefined) query.active = active.toString();

		try {
			const {
				data: { url },
			} = await apiStore.get({
				path: `/report/customer`,
				query,
				isAuthRequired: true,
			});

			return url;
		} catch (e) {}
	},

	async getCustomerContactsReport(
		pool?: string,
		contract?: string,
		active?: string
	): Promise<string | undefined> {
		let query: { [x: string]: string } = {};

		if (pool) query.pool = pool;
		if (contract) query.contract = contract;
		if (active !== undefined) query.active = active;

		try {
			const {
				data: { url },
			} = await apiStore.get({
				path: `/report/customer-user`,
				query,
				isAuthRequired: true,
			});

			return url;
		} catch (e) {}
	},

	async getConsultantsReport(
		pool?: string,
		active?: string,
		branch?: string,
		service?: string,
		certification?: CertificationFilterValue
	): Promise<string | undefined> {
		let query: { [x: string]: string } = { timeZone, ...certificationQuery(certification) };

		if (pool) query.pool = pool;
		if (active !== undefined) query.active = active;
		if (branch) query.branch = branch;
		if (service) query.service = service;

		try {
			const {
				data: { url },
			} = await apiStore.get({
				path: `/report/consultant`,
				query,
				isAuthRequired: true,
			});

			return url;
		} catch (e) {}
	},

	async getCandidatesReport(
		pool?: string,
		active?: string,
		currentAction?: string,
		certification?: CertificationFilterValue
	): Promise<string | undefined> {
		let query: { [x: string]: string } = { timeZone, ...certificationQuery(certification) };

		if (pool) query.pool = pool;
		if (active !== undefined) query.active = active;
		if (currentAction) query.currentAction = currentAction;

		try {
			const {
				data: { url },
			} = await apiStore.get({
				path: `/report/candidate`,
				query,
				isAuthRequired: true,
			});

			return url;
		} catch (e) {}
	},

	async getConsultantsWorkedReport(
		startDate: string,
		endDate: string,
		pool?: string,
		contract?: string,
		customer?: string
	): Promise<string | undefined> {
		let query: { [x: string]: string } = { timeZone, startDate, endDate };

		if (pool) query.pool = pool;
		if (contract) query.contract = contract;
		if (customer) query.customer = customer;

		try {
			const {
				data: { url },
			} = await apiStore.get({
				path: `/report/consultant-worked`,
				query,
				isAuthRequired: true,
			});

			return url;
		} catch (e) {}
	},

	async getSoldHoursAndRevenueReport(
		startDate: string,
		endDate: string,
		pool?: string,
		contract?: string,
		customer?: string,
		status?: string,
		isDebug?: boolean
	): Promise<string | undefined> {
		let query: { [x: string]: string } = { timeZone, startDate, endDate };

		if (pool) query.pool = pool;
		if (contract) query.contract = contract;
		if (customer) query.customer = customer;
		if (status) query.status = status;
		if (isDebug) query.debug = isDebug.toString();

		try {
			const {
				data: { url },
			} = await apiStore.get({
				path: `/report/sold-hours`,
				query,
				isAuthRequired: true,
			});

			return url;
		} catch (e) {}
	},
};
